import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TimePickerField from "../../../common/TimePickerField";
import { Form } from "../../../common/form";
import { z } from "zod";
import { Select } from "../../../common/form/select";
import { Error } from "../../../common/form/error";
import useClassLevel from "src/hooks/useClassLevel";
import useCoach from "src/hooks/useCoach";
import { useRegularClassQuery } from "src/services/react-query-apis/get-regular-class";
import { classLevelOptions, coachOptions } from "./utils";
import { Input } from "src/components/common/form/input";
import moment from "moment";
import { getCurrentMonth } from "src/services/auth/token";

const timeTableFormSchema = regularClassData =>
  z.object({
    start_time: z.coerce.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
      message: "時間を入力してください",
    }),
    end_time: z.coerce.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
      message: "時間を入力してください",
    }),
    max_student:
      regularClassData?.rank_flag !== 1
        ? z.coerce
            .number()
            .optional()
            .transform(val => (!val ? 0 : val))
        : z.coerce.number().min(1, {
            message: "定員は1人以上で入力してください",
          }),
    class_level_id:
      regularClassData?.rank_flag !== 1
        ? z.coerce
            .number()
            .optional()
            .transform(val => (val === 0 ? null : val))
        : z.coerce.number().refine(val => val !== 0, {
            message: "コースレベルを選択してください",
          }),
    coach_id: z.coerce
      .number()
      .optional()
      .transform(val => (val === 0 ? null : val)),
  });

const TimeTableForm = ({ schedule, day, regularClassId, onSubmit }) => {
  const classLevel = useClassLevel();
  const coach = useCoach();

  const regularClassQuery = useRegularClassQuery({
    id: regularClassId,
    queryConfig: {
      enabled: !!regularClassId,
    },
  });

  const isRankClass = regularClassQuery.data?.data?.rank_flag === 1;

  return (
    <Form
      onSubmit={values => {
        const payload = {
          ...values,
          regular_class_id: regularClassId,
          day_week: day,
        };

        onSubmit(payload);
      }}
      schema={timeTableFormSchema(regularClassQuery.data?.data)}
      options={{
        defaultValues: {
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          class_level_id: schedule.class_level_id,
          max_student: schedule.max_student || 0,
          coach_id: schedule.coach_id,
        },
      }}
      className="d-flex justify-content-center"
    >
      {({ setValue, register, formState, watch }) => (
        <div className="d-flex flex-column w-50 time-table-form">
          <div
            className="alert alert-warning mb-3 text-center p-1"
            role="alert"
          >
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
            この変更は{" "}
            {moment(getCurrentMonth())
              .add(1, "month")
              .format("YYYY年MM月")}{" "}
            から適用されます
          </div>
          <div className="day-label text-center mb-3 p-1 rounded bg-light border fw-bold">
            {day}
          </div>
          <div className="d-flex gap-2 flex-column align-items-center">
            <TimePickerField
              name="start_time"
              value={watch("start_time")}
              onChange={e => setValue("start_time", e)}
            />
            <Error errorMessage={formState.errors.start_time?.message} />
            <div style={{ fontSize: "10px" }}>|</div>
            <TimePickerField
              name="end_time"
              value={watch("end_time")}
              onChange={e => setValue("end_time", e)}
            />
            <Error errorMessage={formState.errors.end_time?.message} />
          </div>
          <br />
          {isRankClass && (
            <div className="d-flex gap-2 flex-column align-items-center">
              <div>定員</div>
              <Input
                className="text-center"
                type="number"
                name="max_student"
                registration={register("max_student")}
                error={formState.errors.max_student}
              />
            </div>
          )}
          <div className={`table-select text-center `}>
            コースレベル
            <Select
              label=""
              placeholder=""
              registration={register("class_level_id")}
              options={classLevelOptions(classLevel.list)}
              error={formState.errors.class_level_id}
              readOnly={!isRankClass}
            />
          </div>
          <div className={`mt-1 table-select text-center`}>
            コーチ
            <Select
              label=""
              placeholder=""
              registration={register("coach_id")}
              options={coachOptions(coach.list)}
              error={formState.errors.coach_id}
              readOnly={!isRankClass}
            />
          </div>
          <div className="mt-2 d-flex gap-1 justify-content-center w-100">
            <button className="btn btn-sm btn-primary w-100" type="submit">
              {schedule.id ? "変更確認" : "保存"}
            </button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default TimeTableForm;
